import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { getProductsByCategory } from "../data/products";
import "../assets/styles/CategoryProducts.css";
import { isNewProduct } from "../utils/dateUtils";
import { BaseUrl } from "../config/api";
import axios from "axios";

const CategoryProducts = () => {
  const { categoryName } = useParams();
  const [products, setProducts] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: 10, max: 500 });
  const [openFilter, setOpenFilter] = useState(null);
  const [activeFilters, setActiveFilters] = useState({
    materialAndCare: [],
    color: [],
    size: [],
    categoryName: [],
    priceRange: { min: 0, max: 1000 },
    discount: [],
  });
  const [sortBy, setSortBy] = useState("relevance");
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${BaseUrl}/products/getallproducts`);
        const data = await response.json();
        const categoryProducts = data
          .filter(
            (product) =>
              !product.isDeleted &&
              product.categoryName.toLowerCase().replace(/\s+/g, "-") ===
                categoryName
          )
          .map((product) => ({
            id: product._id,
            name: product.productName,
            price: `Rs. ${product.sellingPrice.toLocaleString()}`,
            images:
              product.images && product.images.length > 0
                ? product.images
                : [product.categoryId?.categotyImage], // Fallback to category image if no product images
            fabric: product.materialAndCare || "Not specified",
            color: product.color || "Not specified",
            size: product.sareeSize
              ? `${product.sareeSize} meters`
              : product.blouseSize
              ? `${product.blouseSize} meters`
              : "Free Size",
            type: product.categoryName,
            dateAdded: new Date().toISOString(),
            discount: calculateDiscount(
              product.actualPrice,
              product.sellingPrice
            ),
            actualPrice: product.actualPrice,
            sellingPrice: product.sellingPrice,
            description: product.description || "",
            stock: product.stock,
            availability: product.availability,
            productCode: product.productCode,
            tags: product.tags || [],
            tax: product.tax,
            couponCode: product.couponCode,
            couponMethod: product.couponMethod,
            variantName: product.variantName || "",
            categoryId: product.categoryId?._id,
          }));
        console.log(categoryProducts);
        setProducts(categoryProducts);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, [categoryName]);

  const calculateDiscount = (actualPrice, sellingPrice) => {
    if (!actualPrice || !sellingPrice) return 0;
    const discount = ((actualPrice - sellingPrice) / actualPrice) * 100;
    return Math.round(discount);
  };

  const handleFilterChange = (filterType, value) => {
    setActiveFilters((prev) => {
      const updatedFilters = { ...prev };
      if (updatedFilters[filterType].includes(value)) {
        updatedFilters[filterType] = updatedFilters[filterType].filter(
          (item) => item !== value
        );
      } else {
        updatedFilters[filterType] = [...updatedFilters[filterType], value];
      }
      return updatedFilters;
    });
  };

  const getSortedProducts = (products) => {
    switch (sortBy) {
      case "price-low-high":
        return [...products].sort((a, b) => {
          const priceA = parseInt(a.price.replace(/[^0-9]/g, ""));
          const priceB = parseInt(b.price.replace(/[^0-9]/g, ""));
          return priceA - priceB;
        });
      case "price-high-low":
        return [...products].sort((a, b) => {
          const priceA = parseInt(a.price.replace(/[^0-9]/g, ""));
          const priceB = parseInt(b.price.replace(/[^0-9]/g, ""));
          return priceB - priceA;
        });
      default:
        return products;
    }
  };

  const toggleMobileFilter = () => {
    setIsMobileFilterOpen(!isMobileFilterOpen);
  };

  const getAvailableFilterOptions = (products) => {
    const options = {
      materialAndCare: new Set(),
      color: new Set(),
      categoryName: new Set(),
      size: new Set(),
    };

    products.forEach((product) => {
      if (product.fabric) options.materialAndCare.add(product.fabric);
      if (product.color) options.color.add(product.color);
      if (product.type) options.categoryName.add(product.type);
      if (product.size) options.size.add(product.size);
    });

    return {
      materialAndCare: Array.from(options.materialAndCare).filter(Boolean),
      color: Array.from(options.color).filter(Boolean),
      categoryName: Array.from(options.categoryName).filter(Boolean),
      size: Array.from(options.size).filter(Boolean),
    };
  };

  const getFilteredProductsCount = (min, max) => {
    return products.filter((product) => {
      const price = parseInt(product.price.replace(/[^0-9]/g, ""));
      return (
        price >= min && price <= max && product.stock > 0 && !product.outOfStock
      );
    }).length;
  };

  const getFilteredProducts = (products) => {
    return products.filter((product) => {
      // Price Range Filter
      const productPrice = parseInt(product.price.replace(/[^0-9]/g, ""));
      if (
        isNaN(productPrice) ||
        productPrice < activeFilters.priceRange.min ||
        productPrice > activeFilters.priceRange.max
      ) {
        return false;
      }

      // Material Filter
      if (
        activeFilters.materialAndCare.length > 0 &&
        !activeFilters.materialAndCare.some((material) =>
          product.fabric.toLowerCase().includes(material.toLowerCase())
        )
      ) {
        return false;
      }

      // Color Filter
      if (
        activeFilters.color.length > 0 &&
        !activeFilters.color.includes(product.color)
      ) {
        return false;
      }

      // Size Filter
      if (activeFilters.size.length > 0) {
        const productSize = product.size.toLowerCase();
        if (
          !activeFilters.size.some((size) =>
            productSize.includes(size.toLowerCase())
          )
        ) {
          return false;
        }
      }

      // Category Filter
      if (
        activeFilters.categoryName.length > 0 &&
        !activeFilters.categoryName.includes(product.type)
      ) {
        return false;
      }

      // Discount Filter - Show products with discounts >= selected threshold
      if (activeFilters.discount.length > 0) {
        const productDiscount = calculateDiscount(
          product.actualPrice,
          product.sellingPrice
        );
        return activeFilters.discount.some(
          (minDiscount) => productDiscount >= parseInt(minDiscount)
        );
      }

      return true;
    });
  };

  const getPriceRange = (products) => {
    const prices = products
      .map((product) => parseInt(product.price.replace(/[^0-9]/g, "")))
      .filter((price) => !isNaN(price));

    return {
      min: Math.min(...prices, 0),
      max: Math.max(...prices, 50000),
    };
  };

  // Add this helper function for USD formatting
  const formatUSD = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Modify the getAvailableDiscountRanges function
  const getAvailableDiscountRanges = (products) => {
    const discountRanges = ["10", "20", "30", "40", "50"];
    return discountRanges.filter((range) => {
      const minDiscount = parseInt(range);
      return products.some((product) => {
        const discount = calculateDiscount(
          product.actualPrice,
          product.sellingPrice
        );
        return discount >= minDiscount;
      });
    });
  };

  return (
    <div className="category-products">
      <div className="breadcrumb">
        <Link to="/">Home</Link> / {categoryName.split("-").join(" ")}
      </div>

      <button className="mobile-filter-button" onClick={toggleMobileFilter}>
        <i className="fas fa-filter"></i> Filters
      </button>

      <div
        className={`mobile-filter-overlay ${
          isMobileFilterOpen ? "active" : ""
        }`}
        onClick={toggleMobileFilter}
      ></div>

      <div className="filters-and-products">
        <div className={`filters ${isMobileFilterOpen ? "active" : ""}`}>
          <button className="close-filter-button" onClick={toggleMobileFilter}>
            ×
          </button>
          <h3>FILTER BY</h3>

          <div className="filter-section">
            <h4
              onClick={() =>
                setOpenFilter(openFilter === "price" ? null : "price")
              }
            >
              PRICE
              <span className="toggle-icon">
                {openFilter === "price" ? "−" : "+"}
              </span>
            </h4>
            {openFilter === "price" && (
              <div className="price-range-container">
                <div className="price-inputs">
                  <div className="input-group">
                    <span className="currency">$</span>
                    <input
                      type="number"
                      placeholder="Min"
                      value={activeFilters.priceRange.min}
                      onChange={(e) => {
                        const value = Math.max(
                          0,
                          parseInt(e.target.value) || 0
                        );
                        setActiveFilters((prev) => ({
                          ...prev,
                          priceRange: {
                            ...prev.priceRange,
                            min: value,
                            max: Math.max(value, prev.priceRange.max),
                          },
                        }));
                      }}
                    />
                  </div>
                  <span className="separator">to</span>
                  <div className="input-group">
                    <span className="currency">$</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={activeFilters.priceRange.max}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setActiveFilters((prev) => ({
                          ...prev,
                          priceRange: {
                            ...prev.priceRange,
                            max: Math.max(value, prev.priceRange.min),
                          },
                        }));
                      }}
                    />
                  </div>
                </div>
                <div className="price-presets">
                  <button
                    className={
                      activeFilters.priceRange.max === 50 &&
                      activeFilters.priceRange.min === 0
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      setActiveFilters((prev) => ({
                        ...prev,
                        priceRange: { min: 0, max: 50 },
                      }))
                    }
                  >
                    Under $50 ({getFilteredProductsCount(0, 50)} items)
                  </button>
                  <button
                    className={
                      activeFilters.priceRange.max === 100 &&
                      activeFilters.priceRange.min === 50
                        ? "active"
                        : ""
                    }
                    onClick={() =>
                      setActiveFilters((prev) => ({
                        ...prev,
                        priceRange: { min: 50, max: 100 },
                      }))
                    }
                  >
                    $50 - $100 ({getFilteredProductsCount(50, 100)} items)
                  </button>
                  <button
                    className={
                      activeFilters.priceRange.min === 100 ? "active" : ""
                    }
                    onClick={() =>
                      setActiveFilters((prev) => ({
                        ...prev,
                        priceRange: { min: 100, max: 1000 },
                      }))
                    }
                  >
                    Above $100 ({getFilteredProductsCount(100, 1000)} items)
                  </button>
                </div>
              </div>
            )}
          </div>

          {getAvailableFilterOptions(products).materialAndCare.length > 0 && (
            <div className="filter-section">
              <h4
                onClick={() =>
                  setOpenFilter(openFilter === "material" ? null : "material")
                }
              >
                MATERIAL
                <span className="toggle-icon">
                  {openFilter === "material" ? "−" : "+"}
                </span>
              </h4>
              {openFilter === "material" && (
                <div className="filter-options">
                  {getAvailableFilterOptions(products).materialAndCare.map(
                    (material) => (
                      <label key={material} className="filter-option">
                        <input
                          type="checkbox"
                          checked={activeFilters.materialAndCare.includes(
                            material
                          )}
                          onChange={() =>
                            handleFilterChange("materialAndCare", material)
                          }
                        />
                        {material}
                      </label>
                    )
                  )}
                </div>
              )}
            </div>
          )}

          {getAvailableFilterOptions(products).color.length > 0 && (
            <div className="filter-section">
              <h4
                onClick={() =>
                  setOpenFilter(openFilter === "color" ? null : "color")
                }
              >
                COLOR
                <span className="toggle-icon">
                  {openFilter === "color" ? "−" : "+"}
                </span>
              </h4>
              {openFilter === "color" && (
                <div className="filter-options">
                  {getAvailableFilterOptions(products).color.map((color) => (
                    <label key={color} className="filter-option">
                      <input
                        type="checkbox"
                        checked={activeFilters.color.includes(color)}
                        onChange={() => handleFilterChange("color", color)}
                      />
                      {color}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {getAvailableFilterOptions(products).categoryName.length > 0 && (
            <div className="filter-section">
              <h4
                onClick={() =>
                  setOpenFilter(openFilter === "category" ? null : "category")
                }
              >
                CATEGORY
                <span className="toggle-icon">
                  {openFilter === "category" ? "−" : "+"}
                </span>
              </h4>
              {openFilter === "category" && (
                <div className="filter-options">
                  {getAvailableFilterOptions(products).categoryName.map(
                    (category) => (
                      <label key={category} className="filter-option">
                        <input
                          type="checkbox"
                          checked={activeFilters.categoryName.includes(
                            category
                          )}
                          onChange={() =>
                            handleFilterChange("categoryName", category)
                          }
                        />
                        {category}
                      </label>
                    )
                  )}
                </div>
              )}
            </div>
          )}

          {products.some(
            (product) =>
              calculateDiscount(product.actualPrice, product.sellingPrice) > 0
          ) && (
            <div className="filter-section">
              <h4
                onClick={() =>
                  setOpenFilter(openFilter === "discount" ? null : "discount")
                }
              >
                DISCOUNT
                <span className="toggle-icon">
                  {openFilter === "discount" ? "−" : "+"}
                </span>
              </h4>
              {openFilter === "discount" && (
                <div className="filter-options">
                  {getAvailableDiscountRanges(products).map((discount) => {
                    const discountCount = products.filter((product) => {
                      const productDiscount = calculateDiscount(
                        product.actualPrice,
                        product.sellingPrice
                      );
                      return productDiscount >= parseInt(discount);
                    }).length;

                    // Only show discount options that have matching products
                    return discountCount > 0 ? (
                      <label key={discount} className="filter-option">
                        <input
                          type="checkbox"
                          checked={activeFilters.discount.includes(discount)}
                          onChange={() =>
                            handleFilterChange("discount", discount)
                          }
                        />
                        {discount}% & above ({discountCount} items)
                      </label>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        <div>
          <div className="results-sort-section">
            <div className="results-count">
              {
                getSortedProducts(getFilteredProducts(products)).filter(
                  (product) => product.stock > 0 && !product.outOfStock
                ).length
              }{" "}
              {getSortedProducts(getFilteredProducts(products)).filter(
                (product) => product.stock > 0 && !product.outOfStock
              ).length === 1
                ? "Result"
                : "Results"}
            </div>

            <div className="sort-section">
              <label>SORT BY : </label>
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="relevance">Relevance</option>
                <option value="price-low-high">Price: Low to High</option>
                <option value="price-high-low">Price: High to Low</option>
              </select>
            </div>
          </div>

          <div className="products-grid">
            {getSortedProducts(getFilteredProducts(products))
              .filter((product) => product.stock > 0 && !product.outOfStock)
              .map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryProducts;
